import React, { Component } from 'react';
import posthog from "posthog-js";
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, ReferenceLine } from 'recharts';
import StateConstants from "src/states/StateContstants";
import './ValuationTimeSeriesChart.css';

interface ValuationTimeSeriesChartProps {
    timeSeries: {
        v: number[];
        d: string[];
        tu?: number;
    } | null;
    width?: number;
    height?: number;
    isPremium?: boolean;
    onUpgradeClick?: () => void;
    stateHandler?: any;
}

interface ValuationTimeSeriesChartState {
    selectedPeriod: '1M' | '6M' | '5Y' | 'ALL';
}

type TimePeriod = '1M' | '6M' | '5Y' | 'ALL';

class ValuationTimeSeriesChart extends Component<ValuationTimeSeriesChartProps, ValuationTimeSeriesChartState> {
    constructor(props: ValuationTimeSeriesChartProps) {
        super(props);
        this.state = {
            selectedPeriod: '6M'
        };
    }

    formatCurrency = (amount: number): string => {
        return new Intl.NumberFormat('en-GB', {
            style: 'currency',
            currency: 'EUR',
            minimumFractionDigits: 0,
            maximumFractionDigits: 0
        }).format(amount);
    };

    formatDate = (dateString: string): string => {
        const date = new Date(dateString);
        return date.toLocaleDateString('en-GB', {
            year: 'numeric',
            month: 'short'
        });
    };

    formatDateForAxis = (dateString: string, period: TimePeriod): string => {
        const date = new Date(dateString);
        
        switch (period) {
            case '1M':
                // For 1 month, show day and month
                return date.toLocaleDateString('en-GB', {
                    day: 'numeric',
                    month: 'short'
                });
            case '6M':
                // For 6 months, show month and year
                return date.toLocaleDateString('en-GB', {
                    month: 'short',
                    year: '2-digit'
                });
            case '5Y':
                // For 5 years, show month and year
                return date.toLocaleDateString('en-GB', {
                    month: 'short',
                    year: 'numeric'
                });
            case 'ALL':
            default:
                // For all time, show month and year
                return date.toLocaleDateString('en-GB', {
                    month: 'short',
                    year: 'numeric'
                });
        }
    };

    getFilteredData = (period: TimePeriod) => {
        const { timeSeries } = this.props;
        if (!timeSeries || timeSeries.v.length === 0) return [];

        const now = new Date();
        let cutoffDate: Date;

        switch (period) {
            case '1M':
                cutoffDate = new Date(now.getFullYear(), now.getMonth() - 1, now.getDate());
                break;
            case '6M':
                cutoffDate = new Date(now.getFullYear(), now.getMonth() - 6, now.getDate());
                break;
            case '5Y':
                cutoffDate = new Date(now.getFullYear() - 5, now.getMonth(), now.getDate());
                break;
            case 'ALL':
            default:
                cutoffDate = new Date(0); // Beginning of time
                break;
        }

        const filteredData = timeSeries.v.map((value, index) => ({
            date: timeSeries.d[index],
            dateValue: new Date(timeSeries.d[index]).getTime(), // Convert to timestamp for proper spacing
            value: value,
            formattedDate: this.formatDate(timeSeries.d[index]),
            formattedValue: this.formatCurrency(value)
        })).filter(item => new Date(item.date) >= cutoffDate);

        return filteredData;
    };

    getSmartTicks = (data: any[]) => {
        if (data.length === 0) return [];
        
        const values = data.map(d => d.value);
        const minValue = Math.min(...values);
        const maxValue = Math.max(...values);
        const range = maxValue - minValue;
        
        // Calculate smart tick intervals using logical increments
        let tickInterval: number;
        
        // Determine appropriate tick interval using a logarithmic scale
        let logRange = Math.log10(range);
        let logRangeFloor = Math.max(1, Math.floor(logRange) - 1);
        const baseIntervals = [1, 2, 5, 10]; // Possible base intervals
        let startTickInterval = Math.pow(10, logRangeFloor);
        tickInterval = startTickInterval; // Default to power of 10

        // Check if multiples of 1, 2, or 5 by powers of 10 are more appropriate
        for (const base of baseIntervals) {
            const potentialInterval = base * startTickInterval;
            const numberOfTicks = range / potentialInterval;
            if (numberOfTicks <= 8 && numberOfTicks > 3) { // Ensure a reasonable number of ticks
                tickInterval = potentialInterval;
                break;
            }
        }

        const ticks = [];
        const startTick = Math.floor(minValue / tickInterval) * tickInterval;
        const endTick = Math.floor(maxValue / tickInterval) * tickInterval;
        
        for (let tick = startTick; tick <= endTick; tick += tickInterval) {
            // Only include ticks that are within or above the data range
            if (tick >= minValue) {
                ticks.push(tick);
            }
        }
        
        return ticks;
    };

    getSmartDateTicks = (data: any[], period: TimePeriod) => {
        if (data.length === 0) return [];
        
        const dates = data.map(d => new Date(d.date));
        const minDate = new Date(Math.min(...dates.map(d => d.getTime())));
        const maxDate = new Date(Math.max(...dates.map(d => d.getTime())));
        
        const ticks = [];
        const current = new Date(minDate);
        
        // Calculate appropriate interval based on period and data range
        let intervalDays: number;
        const totalDays = Math.ceil((maxDate.getTime() - minDate.getTime()) / (1000 * 60 * 60 * 24));
        
        // Handle edge case where all data is on the same day
        if (totalDays === 0) {
            return [minDate];
        }
        
        switch (period) {
            case '1M':
                intervalDays = Math.max(1, Math.floor(totalDays / 4)); // Show ~5 ticks max
                break;
            case '6M':
                intervalDays = Math.max(7, Math.floor(totalDays / 4)); // Show ~5 ticks max
                break;
            case '5Y':
                intervalDays = Math.max(30, Math.floor(totalDays / 4)); // Show ~5 ticks max
                break;
            case 'ALL':
            default:
                intervalDays = Math.max(90, Math.floor(totalDays / 3)); // Show ~4 ticks max
                break;
        }
        
        while (current <= maxDate) {
            ticks.push(new Date(current));
            current.setDate(current.getDate() + intervalDays);
        }
        
        // Ensure we always include the max date
        if (ticks.length === 0 || ticks[ticks.length - 1].getTime() !== maxDate.getTime()) {
            ticks.push(new Date(maxDate));
        }
        
        return ticks;
    };

    handlePeriodChange = (period: TimePeriod) => {
        this.setState({ selectedPeriod: period });
    };

    render() {
        const { timeSeries } = this.props;
        const { selectedPeriod } = this.state;
        
        if (!timeSeries || timeSeries.v.length === 0) {
            return (
                <div className="valuation-chart-container">
                    <div className="chart-placeholder">
                        <span>📊</span>
                        <span>No valuation history available</span>
                    </div>
                </div>
            );
        }

        const data = this.getFilteredData(selectedPeriod);
        const ticks = this.getSmartTicks(data);
        const dateTicks = this.getSmartDateTicks(data, selectedPeriod);
        
        // Get the current price (last data point)
        const currentPrice = data.length > 0 ? data[data.length - 1].value : null;

        return (
            <div className="valuation-chart-container">
                <div className="chart-header">
                    <div className="period-selector">
                        {(['1M', '6M', '5Y', 'ALL'] as TimePeriod[]).map(period => (
                            <button
                                key={period}
                                className={`period-button ${selectedPeriod === period ? 'active' : ''}`}
                                onClick={() => this.handlePeriodChange(period)}
                            >
                                {period}
                            </button>
                        ))}
                    </div>
                </div>
                
                <div className="chart-wrapper">
                    <ResponsiveContainer width="100%" height={220}>
                        <LineChart
                            data={data}
                            margin={{ top: 10, right: 75, left: 10, bottom: 10 }}
                        >
                            <CartesianGrid 
                                strokeDasharray="3 3" 
                                stroke="#f0f0f0"
                                strokeWidth={1}
                            />
                            <XAxis 
                                className="x-axis"
                                tickMargin={20}
                                dataKey="dateValue"
                                type="number"
                                scale="time"
                                domain={['dataMin', 'dataMax']}
                                ticks={dateTicks.map(d => d.getTime())}
                                tickFormatter={(tick) => {
                                    const date = new Date(tick);
                                    return this.formatDateForAxis(date.toISOString(), selectedPeriod);
                                }}
                                stroke="#666"
                                fontSize={12}
                                tickLine={false}
                                axisLine={false}
                            />
                            <YAxis 
                                domain={['dataMin - (dataMax - dataMin) * 0.1', 'dataMax + (dataMax - dataMin) * 0.1']}
                                ticks={ticks}
                                tickMargin={20}
                                tickFormatter={this.formatCurrency}
                                stroke="#666"
                                fontSize={12}
                                tickLine={false}
                                axisLine={false}
                            />
                            <Tooltip
                                formatter={(value: number, name: string, props: any) => [
                                    this.formatCurrency(value), 
                                    'Valuation'
                                ]}
                                labelFormatter={(label, payload) => {
                                    if (payload && payload.length > 0) {
                                        const data = payload[0].payload;
                                        return `Date: ${data.date}`;
                                    }
                                    return `Date: ${label}`;
                                }}
                                contentStyle={{
                                    backgroundColor: 'white',
                                    border: '1px solid #e5e7eb',
                                    borderRadius: '8px',
                                    boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
                                    fontSize: '14px',
                                }}
                                labelStyle={{
                                    color: 'grey',
                                }}
                                itemStyle={{
                                    color: 'var(--theme-identifier-color)',
                                }}
                            />
                            <Line
                                type="monotone"
                                dataKey="value"
                                stroke="#D5D5D5"
                                strokeWidth={2}
                                dot={{ fill: 'black', stroke: 'black', strokeWidth: 1, r: 1 }}
                                activeDot={{ r: 6, fill: 'var(--theme-identifier-color)', stroke: 'var(--theme-identifier-color)', strokeWidth: 2 }}
                                strokeLinecap="round"
                                strokeLinejoin="round"
                            />
                            {currentPrice && (
                                <ReferenceLine 
                                    y={currentPrice}
                                    stroke="transparent"
                                    strokeWidth={0}
                                    label={
                                        {
                                        value: this.formatCurrency(currentPrice),
                                        position: "right",
                                        style: {
                                            fill: 'grey',
                                            fontSize: '12px',
                                            fontWeight: 'bold',
                                            padding: '4px 8px',
                                            borderRadius: '8px',
                                            border: '1px solid black',
                                        }
                                    }}
                                />
                            )}
                        </LineChart>
                    </ResponsiveContainer>
                </div>
            </div>
        );
    }
}

export default ValuationTimeSeriesChart;
